import React, { useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { Contract, ContractDocument } from '../../types';
import Input from './Input';
import Select from './Select';
import AsyncSelect from './AsyncSelect';
import TextArea from './TextArea';
import Button from './Button';
import ContractDocumentsSection from './ContractDocumentsSection';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { uploadFiles } from '../../services/upload.service';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';
import {
  fetchClients,
  fetchProviders,
  fetchBranches,
  contractTypeOptions,
  statusOptions,
} from './contractFormOptions';
import { useContractCurrency } from './useContractCurrency';

interface ContractFormProps {
  contract?: Contract;
  onSubmit: (data: Partial<Contract>) => void;
  onCancel: () => void;
}

const ContractForm: React.FC<ContractFormProps> = ({ contract, onSubmit, onCancel }) => {
  const [existingDocuments, setExistingDocuments] = useState<ContractDocument[]>(
    Array.isArray(contract?.documents) ? (contract!.documents as ContractDocument[]).filter(d => d && typeof d === 'object') : []
  );
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    control
  } = useForm<Contract>({
    defaultValues: contract ? {
      ...contract,
      currency: resolveCurrencyDocumentId(contract.currency),
      startDate: formatDateForInput(contract.startDate),
      endDate: formatDateForInput(contract.endDate)
    } : {
      title: '',
      type: '',
      clientId: '',
      providerId: '',
      startDate: '',
      endDate: '',
      value: 0,
      currency: '',
      status: 'active',
      description: '',
      branchId: '',
      notes: ''
    }
  });

  const { currencyOptions, selectedCurrency, isForeign } = useContractCurrency(contract, control, setValue);

  // Watch the party type to show appropriate dropdown
  const selectedType = useWatch({
    control,
    name: 'type',
    defaultValue: contract?.type || ''
  });

  const handleFormSubmit = async (data: Contract) => {
    // Upload any newly selected files first, then store them in the documents json
    let documents: ContractDocument[] = [...existingDocuments];
    if (newFiles.length > 0) {
      try {
        setIsUploading(true);
        const uploaded = await uploadFiles(newFiles);
        documents = [...documents, ...uploaded];
      } catch (error: any) {
        alert(error?.message || 'Failed to upload contract documents');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    // Clean up data based on contract type
    const submitData = {
      ...data,
      startDate: formatDateForAPI(data.startDate),
      endDate: formatDateForAPI(data.endDate),
      // EGP contracts always use rate 1; foreign ones use the entered rate
      exchangeRate: isForeign ? Number(data.exchangeRate) || 1 : 1,
      documents,
      // If it's a client contract, remove providerId
      ...(data.type === 'Charter' || data.type === 'Service' ? { providerId: undefined } : {}),
      // If it's a supplier contract, remove clientId
      ...(data.type === 'Supply' || data.type === 'Maintenance' ? { clientId: undefined } : {})
    };
    
    onSubmit({
      ...submitData,
      ...(contract ? { id: contract.id, documentId: contract.documentId } : {}),
      createdAt: contract?.createdAt || new Date().toISOString()
    });
  };

  const isClientContract = selectedType === 'Charter' || selectedType === 'Service';
  const isSupplierContract = selectedType === 'Supply' || selectedType === 'Maintenance';

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {contract ? 'Edit Contract' : 'Add New Contract'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Contract Title"
            {...register('title', { 
              required: 'Contract title is required',
              minLength: { value: 3, message: 'Title must be at least 3 characters' }
            })}
            error={errors.title?.message}
            placeholder="Enter contract title"
          />

          <Select
            label="Contract Type"
            {...register('type', { required: 'Contract type is required' })}
            options={contractTypeOptions}
            error={errors.type?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Show client dropdown for client contracts */}
          {isClientContract && (
            <Controller
              name="clientId"
              control={control}
              rules={{ required: 'Client is required for this contract type' }}
              render={({ field }) => (
                <AsyncSelect
                  label="Client"
                  name="clientId"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.clientId?.message}
                  required
                  placeholder="Select client..."
                  fetchOptions={fetchClients}
                  getOptionLabel={(client) => client.name}
                  getOptionValue={(client) => client.documentId || client.id?.toString()}
                />
              )}
            />
          )}

          {/* Show provider dropdown for supplier contracts */}
          {isSupplierContract && (
            <Controller
              name="providerId"
              control={control}
              rules={{ required: 'Supplier is required for this contract type' }}
              render={({ field }) => (
                <AsyncSelect
                  label="Supplier"
                  name="providerId"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.providerId?.message}
                  required
                  placeholder="Select supplier..."
                  fetchOptions={fetchProviders}
                  getOptionLabel={(provider) => provider.name}
                  getOptionValue={(provider) => provider.documentId || provider.id?.toString()}
                />
              )}
            />
          )}

          <Select
            label="Status"
            {...register('status', { required: 'Status is required' })}
            options={statusOptions}
            error={errors.status?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Start Date"
            type="date"
            {...register('startDate', { required: 'Start date is required' })}
            error={errors.startDate?.message}
          />

          <Input
            label="End Date"
            type="date"
            {...register('endDate', { 
              required: 'End date is required',
              validate: (value, formValues) => {
                if (formValues.startDate && value <= formValues.startDate) {
                  return 'End date must be after start date';
                }
                return true;
              }
            })}
            error={errors.endDate?.message}
          />
        </div>

        <div className={`grid grid-cols-1 gap-6 ${isForeign ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <Input
            label="Contract Value"
            type="number"
            step="0.01"
            {...register('value', { 
              required: 'Contract value is required',
              min: { value: 0.01, message: 'Value must be greater than 0' }
            })}
            error={errors.value?.message}
            placeholder="0.00"
          />

          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            placeholder="Select currency"
            error={errors.currency?.message as string}
          />

          {isForeign && (
            <Input
              label={`Rate (${selectedCurrency?.code || ''} to EGP)`}
              type="number"
              step="0.0001"
              {...register('exchangeRate', {
                required: 'Rate is required for non-EGP currencies',
                min: { value: 0.0001, message: 'Rate must be greater than 0' }
              })}
              error={errors.exchangeRate?.message}
              placeholder="e.g. 50"
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="branchId"
            control={control}
            render={({ field }) => (
              <AsyncSelect
                label="Vessel / Headquarters"
                name="branchId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.branchId?.message}
                placeholder="No specific branch..."
                fetchOptions={fetchBranches}
                getOptionLabel={(branch) => `${branch.name}${branch.type ? ` (${branch.type})` : ''}`}
                getOptionValue={(branch) => branch.documentId || branch.id?.toString()}
              />
            )}
          />
        </div>

        <TextArea
          label="Description"
          {...register('description', {
            required: 'Description is required',
            minLength: { value: 10, message: 'Description must be at least 10 characters' }
          })}
          error={errors.description?.message}
          placeholder="Enter contract description"
          rows={3}
        />

        <ContractDocumentsSection
          existingDocuments={existingDocuments}
          newFiles={newFiles}
          onRemoveExisting={(index) => setExistingDocuments(prev => prev.filter((_, i) => i !== index))}
          onAddFiles={(files) => setNewFiles(prev => [...prev, ...files])}
          onRemoveNewFile={(index) => setNewFiles(prev => prev.filter((_, i) => i !== index))}
        />

        <TextArea
          label="Notes (Optional)"
          {...register('notes')}
          error={errors.notes?.message}
          placeholder="Enter any additional notes"
          rows={2}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || isUploading}
          >
            {isUploading ? 'Uploading files...' : contract ? 'Update Contract' : 'Create Contract'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;